/**
 * Forest Journey Map Component
 * 
 * An interactive visual map showing the 6-chapter journey through the forest.
 * Features:
 * - Vertical path with emoji markers for each chapter location
 * - Animated paw prints tracing the journey path
 * - Floating/bouncing animation on emoji icons (like homepage)
 * - Click to navigate to any chapter
 * - Visual progress indicator showing current chapter
 * - ✨ NEW: "Walking footprints" animation - footprints fade in and walk to clicked chapter before navigation
 * 
 * Used as "Spread 0" / Chapter Opening for the book experience.
 */

import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Home } from "lucide-react";

interface ChapterLocation {
  id: number;
  emoji: string;
  title: string;
  subtitle: string;
  route: string;
  position: { x: string; y: string };
}

const CHAPTER_LOCATIONS: ChapterLocation[] = [
  {
    id: 1,
    emoji: "🌊",
    title: "Chapter 1",
    subtitle: "The Still Pond",
    route: "/book/chapter/1/story",
    position: { x: "20%", y: "15%" } // Chapter 1: Left side, top
  },
  {
    id: 2,
    emoji: "🦎",
    title: "Chapter 2",
    subtitle: "The Colorful Chameleon",
    route: "/book/chapter/2/story",
    position: { x: "80%", y: "15%" } // Chapter 2: Right side, same line as Chapter 1
  },
  {
    id: 3,
    emoji: "🌉",
    title: "Chapter 3",
    subtitle: "The Brave Bridge",
    route: "/book/chapter/3/story",
    position: { x: "50%", y: "35%" } // Chapter 3: Middle, below Chapter 1 & 2
  },
  {
    id: 4,
    emoji: "🌸",
    title: "Chapter 4",
    subtitle: "The Kindness Meadow",
    route: "/book/chapter/4/story",
    position: { x: "50%", y: "55%" } // Chapter 4: Middle, below Chapter 3
  },
  {
    id: 5,
    emoji: "🌻",
    title: "Chapter 5",
    subtitle: "The Gratitude Garden",
    route: "/book/chapter/5/story",
    position: { x: "20%", y: "75%" } // Chapter 5: Lower left, higher up
  },
  {
    id: 6,
    emoji: "🌳",
    title: "Chapter 6",
    subtitle: "The Sharing Tree",
    route: "/book/chapter/6/story",
    position: { x: "80%", y: "75%" } // Chapter 6: Lower right, higher up (below Chapter 3)
  }
];

interface ForestJourneyMapProps {
  currentChapter?: number;
  completedChapters?: number[];
}

export function ForestJourneyMap({ 
  currentChapter = 1, 
  completedChapters = []
}: ForestJourneyMapProps) {
  const navigate = useNavigate();
  const [hoveredChapter, setHoveredChapter] = useState<number | null>(null);
  
  // ✨ NEW: Walking footprints animation state
  const [isWalking, setIsWalking] = useState(false);
  const [walkingPath, setWalkingPath] = useState<{ from: number; to: number } | null>(null);

  /**
   * ✨ NEW: Animated walking footprints before navigation
   * 
   * When user clicks a chapter:
   * 1. Calculate path from current chapter to clicked chapter
   * 2. Animate footprints along that path (fade in + walk)
   * 3. After animation completes, navigate to the chapter
   */
  const handleChapterClick = async (route: string, chapterId: number) => {
    // If clicking current chapter, navigate immediately (no animation needed)
    if (chapterId === currentChapter) {
      const targetUrl = `${route}?spread=1`;
      navigate(targetUrl);
      return;
    }

    // ✨ Start walking animation
    setIsWalking(true);
    setWalkingPath({ from: currentChapter, to: chapterId });

    console.log(`🐾 Walking animation: Chapter ${currentChapter} → Chapter ${chapterId}`);

    // Calculate animation duration based on distance
    const distance = Math.abs(chapterId - currentChapter);
    const animationDuration = 800 + (distance * 400); // 800ms base + 400ms per chapter

    // Wait for animation to complete
    await new Promise(resolve => setTimeout(resolve, animationDuration));

    // ✨ NEW: Brief pause after arrival to appreciate the moment
    console.log(`🎯 Footprints arrived at Chapter ${chapterId}! Pausing before navigation...`);
    await new Promise(resolve => setTimeout(resolve, 1200)); // 1.2 second pause after arrival

    // Navigate after animation + pause
    const targetUrl = `${route}?spread=1`;
    console.log(`📍 Map navigation: Starting Chapter ${chapterId}`);
    navigate(targetUrl);

    // Reset animation state
    setIsWalking(false);
    setWalkingPath(null);
  };

  const handleHomeClick = () => {
    navigate("/book/wtbtg");
  };

  /**
   * ✨ Calculate footprints along walking path
   * Returns array of positions for animated footprints
   */
  const getWalkingFootprints = () => {
    if (!walkingPath) return [];

    const { from, to } = walkingPath;
    const start = from - 1; // Convert to 0-based index
    const end = to - 1;
    
    const footprints: { x: number; y: number; angle: number; delay: number }[] = [];
    
    // Determine direction (forward or backward through chapters)
    const direction = end > start ? 1 : -1;
    const steps = Math.abs(end - start);

    // Generate footprints along the path
    // ✅ FIX: Loop should stop BEFORE going past the destination
    for (let step = 0; step < steps; step++) {
      const currentChapterIndex = start + (step * direction);
      const nextChapterIndex = currentChapterIndex + direction;

      if (nextChapterIndex < 0 || nextChapterIndex >= CHAPTER_LOCATIONS.length) continue;

      const currentLoc = CHAPTER_LOCATIONS[currentChapterIndex];
      const nextLoc = CHAPTER_LOCATIONS[nextChapterIndex];

      const x1 = parseFloat(currentLoc.position.x);
      const y1 = parseFloat(currentLoc.position.y);
      const x2 = parseFloat(nextLoc.position.x);
      const y2 = parseFloat(nextLoc.position.y);

      // Calculate angle for footprint direction
      const deltaX = x2 - x1;
      const deltaY = y2 - y1;
      const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

      // Create multiple footprints along this segment
      const numPrintsPerSegment = 6;
      for (let i = 0; i <= numPrintsPerSegment; i++) {
        const progress = i / numPrintsPerSegment;
        const x = x1 + (x2 - x1) * progress;
        const y = y1 + (y2 - y1) * progress;
        const delay = (step * numPrintsPerSegment + i) * 0.08; // 80ms between each footprint

        footprints.push({ x, y, angle, delay });
      }
    }

    return footprints;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-forest-50 via-cream-50 to-golden-100 relative overflow-hidden">
      {/* Home Button */}
      <div className="fixed top-6 left-6 z-50">
        <Button
          onClick={handleHomeClick}
          variant="outline"
          size="lg"
          className="bg-white/90 backdrop-blur-sm border-forest-200 text-forest-700 hover:bg-forest-50 shadow-lg"
        >
          <Home className="w-5 h-5 mr-2" />
          Back to Contents
        </Button>
      </div>

      {/* Celebration Animation - Confetti/Balloons FROM TOP */}
      <motion.div
        className="fixed inset-0 pointer-events-none z-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-4xl"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-10%'
            }}
            animate={{
              y: [0, 800],
              x: [0, (Math.random() - 0.5) * 100],
              rotate: [0, 360],
              opacity: [1, 0]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              delay: Math.random() * 2,
              repeat: Infinity,
              repeatDelay: 5
            }}
          >
            {['🎈', '🎉', '✨', '🌟', '🎊'][Math.floor(Math.random() * 5)]}
          </motion.div>
        ))}
      </motion.div>

      {/* Page Title */}
      <div className="text-center pt-20 pb-10 relative z-10">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-5xl font-bold text-forest-800 mb-4"
        >
          🌲 Your Journey through the Magical Forest 🌲
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-xl text-forest-600"
        >
          Click on the glowing icon below to start your next adventure
        </motion.p>
      </div>

      {/* Interactive Forest Map - Full viewport height with all 6 chapters visible */}
      <div className="relative w-full mx-auto px-8" style={{ height: "calc(100vh - 250px)", minHeight: "600px", maxWidth: "1400px" }}>
        {/* Animated Paw Print Trail (Static - shows completed progress) */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 1 }}
        >
          {CHAPTER_LOCATIONS.slice(0, -1).map((location, index) => {
            const nextLocation = CHAPTER_LOCATIONS[index + 1];
            return (
              <motion.line
                key={`path-${location.id}`}
                x1={location.position.x}
                y1={location.position.y}
                x2={nextLocation.position.x}
                y2={nextLocation.position.y}
                stroke="hsl(var(--forest-300))"
                strokeWidth="3"
                strokeDasharray="10,10"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.5 }}
                transition={{ 
                  duration: 1.5, 
                  delay: index * 0.3,
                  ease: "easeInOut"
                }}
              />
            );
          })}
        </svg>

        {/* Static Footprint Path Along Journey (Shows Progress) */}
        {CHAPTER_LOCATIONS.slice(0, -1).map((location, index) => {
          const nextLocation = CHAPTER_LOCATIONS[index + 1];
          
          const isCurrentPath = (location.id < currentChapter) || 
                                (location.id === currentChapter && completedChapters.includes(location.id));
          const shouldShowPath = isCurrentPath;
          
          const x1 = parseFloat(location.position.x);
          const y1 = parseFloat(location.position.y);
          const x2 = parseFloat(nextLocation.position.x);
          const y2 = parseFloat(nextLocation.position.y);
          
          const deltaX = x2 - x1;
          const deltaY = y2 - y1;
          const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
          
          const numFootprints = 8;
          const footprints = [];
          
          for (let i = 0; i <= numFootprints; i++) {
            const progress = i / numFootprints;
            const x = x1 + (x2 - x1) * progress;
            const y = y1 + (y2 - y1) * progress;
            
            footprints.push(
              <motion.div
                key={`footprint-${location.id}-${i}`}
                className="absolute pointer-events-none"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  zIndex: 2
                }}
              >
                {/* Glowing Blue Halo Effect */}
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{
                    width: "40px",
                    height: "40px",
                    left: "-8px",
                    top: "-8px",
                    background: "radial-gradient(circle, rgba(59, 130, 246, 0.6) 0%, rgba(59, 130, 246, 0.3) 50%, transparent 70%)",
                    filter: "blur(8px)"
                  }}
                  initial={{ 
                    opacity: 0,
                    scale: 0
                  }}
                  animate={{ 
                    opacity: shouldShowPath ? [0, 1, 0.8, 1] : 0,
                    scale: shouldShowPath ? [0.5, 1.2, 1] : 0
                  }}
                  transition={{ 
                    delay: 2.5 + index * 0.4 + i * 0.15,
                    duration: 1.2,
                    ease: "easeOut"
                  }}
                />
                
                {/* Footprint Emoji */}
                <motion.div
                  className="relative text-2xl"
                  style={{
                    filter: shouldShowPath ? "drop-shadow(0 0 8px rgba(59, 130, 246, 0.8))" : "none"
                  }}
                  initial={{ 
                    opacity: 0, 
                    scale: 0,
                    filter: "blur(4px)",
                    rotate: angle
                  }}
                  animate={{ 
                    opacity: shouldShowPath ? 0.7 : 0,
                    scale: shouldShowPath ? 1 : 0,
                    filter: shouldShowPath ? "blur(0px)" : "blur(4px)",
                    rotate: angle
                  }}
                  transition={{ 
                    delay: 2.5 + index * 0.4 + i * 0.15,
                    duration: 1.2,
                    ease: "easeOut"
                  }}
                >
                  🐾
                </motion.div>
              </motion.div>
            );
          }
          
          return footprints;
        }).flat()}

        {/* ✨ NEW: Walking Footprints Animation (Plays when clicking a chapter) */}
        <AnimatePresence>
          {isWalking && walkingPath && getWalkingFootprints().map((footprint, index) => (
            <motion.div
              key={`walking-${index}`}
              className="absolute pointer-events-none"
              style={{
                left: `${footprint.x}%`,
                top: `${footprint.y}%`,
                zIndex: 20 // Above static footprints
              }}
              initial={{ 
                opacity: 0,
                scale: 0,
                rotate: footprint.angle
              }}
              animate={{ 
                opacity: [0, 1, 0.9],
                scale: [0, 1.2, 1],
                y: [0, -5, 0]
              }}
              exit={{ 
                opacity: 0,
                scale: 0
              }}
              transition={{ 
                delay: footprint.delay,
                duration: 0.5,
                ease: "easeOut"
              }}
            >
              {/* ✨ Golden Glow for Walking Footprints */}
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                  width: "50px",
                  height: "50px",
                  left: "-12px",
                  top: "-12px",
                  background: "radial-gradient(circle, rgba(251, 191, 36, 0.8) 0%, rgba(251, 191, 36, 0.4) 50%, transparent 70%)",
                  filter: "blur(10px)"
                }}
                animate={{ 
                  scale: [1, 1.3, 1],
                  opacity: [0.8, 1, 0.8]
                }}
                transition={{ 
                  duration: 0.6,
                  repeat: 2,
                  ease: "easeInOut"
                }}
              />
              
              {/* 🐾 Walking Footprint (Golden Color) */}
              <motion.div
                className="relative text-3xl"
                style={{
                  filter: "drop-shadow(0 0 12px rgba(251, 191, 36, 1))"
                }}
              >
                🐾
              </motion.div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Chapter Location Markers */}
        {CHAPTER_LOCATIONS.map((location, index) => {
          const isCompleted = completedChapters.includes(location.id);
          const isCurrent = currentChapter === location.id;
          const isHovered = hoveredChapter === location.id;

          return (
            <motion.div
              key={`chapter-${location.id}`}
              className="absolute"
              style={{
                left: location.position.x,
                top: location.position.y,
                transform: "translate(-50%, -50%)",
                zIndex: 10
              }}
              initial={{ opacity: 0, scale: 0, y: -50 }}
              animate={{ 
                opacity: 1, 
                scale: 1, 
                y: 0
              }}
              transition={{ 
                delay: index * 0.3 + 0.5,
                type: "spring",
                stiffness: 200
              }}
            >
              <motion.button
                onClick={() => handleChapterClick(location.route, location.id)}
                onMouseEnter={() => setHoveredChapter(location.id)}
                onMouseLeave={() => setHoveredChapter(null)}
                className="relative group cursor-pointer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                disabled={isWalking} // ✨ Disable clicks during walking animation
              >
                {/* Glowing Halo for Current Chapter */}
                {isCurrent && (
                  <motion.div
                    className="absolute inset-0 rounded-full bg-golden-400/30"
                    style={{ width: "120px", height: "120px", left: "-10px", top: "-10px" }}
                    animate={{ 
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3]
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                )}

                {/* Emoji Icon with Floating Animation */}
                <motion.div
                  className={`
                    relative w-24 h-24 rounded-full flex items-center justify-center text-6xl
                    transition-all duration-300
                    ${isCurrent ? "bg-golden-200 shadow-2xl" : "bg-cream-100 shadow-lg"}
                    ${isCompleted ? "opacity-100" : "opacity-90"}
                    group-hover:shadow-2xl
                  `}
                  animate={{ 
                    y: [-8, 8, -8],
                    rotate: [-2, 2, -2]
                  }}
                  transition={{ 
                    duration: 4, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    delay: index * 0.5
                  }}
                >
                  {location.emoji}
                  
                  {/* Completion Checkmark */}
                  {isCompleted && (
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg">
                      ✓
                    </div>
                  )}
                </motion.div>

                {/* Chapter Info Card (Shows on Hover) */}
                {(isHovered || isCurrent) && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute top-full mt-4 left-1/2 -translate-x-1/2 w-64 pointer-events-none"
                  >
                    <Card className="bg-white/95 backdrop-blur-sm border-forest-200 shadow-xl">
                      <div className="p-4 text-center">
                        <Badge className="mb-2 bg-forest-600 text-white">
                          {location.title}
                        </Badge>
                        <p className="text-base font-semibold text-forest-800">
                          {location.subtitle}
                        </p>
                        {isCurrent && (
                          <p className="text-xs text-golden-600 mt-2 font-medium">
                            ← You are here
                          </p>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                )}
              </motion.button>
            </motion.div>
          );
        })}
      </div>

      {/* Footer Instructions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="text-center pb-10 mt-10"
      >
        <p className="text-forest-600 text-lg">
          ✨ Follow the paw prints to discover each chapter ✨
        </p>
      </motion.div>
    </div>
  );
}
